// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Hook for fetching the SFDC drift discrepancies for one Hardfin
 * entity. Gated by `enabled` so the popover trigger can defer the
 * fetch until the user opens the drift-resolution UI.
 */
import { getSFDCDriftDiff } from '@/sfdc/api/drift';
import { SFDCDriftEntityType } from '@/sfdc/api/types';
import { sfdcDriftDiffQueryKey } from '@/sfdc/hooks/query-keys';
import { useQuery } from '@tanstack/react-query';

interface Options {
  /**
   * When false the query is skipped — useful for gating on
   * `integration.needsReview` so non-drifted entities never fetch.
   */
  enabled?: boolean;
}

export function useSFDCDriftDiff(
  entityType: SFDCDriftEntityType,
  entityKey: string,
  options: Options = {}
) {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: sfdcDriftDiffQueryKey(entityType, entityKey),
    queryFn: () => getSFDCDriftDiff(entityType, entityKey),
    enabled,
  });

  return {
    fields: query.data?.data.fields ?? [],
    dismissed: query.data?.data.dismissed ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  };
}
